import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lightbulb } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';

export interface GenerateHeatsError {
  error: string;
  /** Absents quand le refus ne porte pas sur le PLACEMENT (données à compléter). */
  total_heats_needed?: number;
  total_heats_placed?: number;
  shortage_heats?: number;
  suggestions: string[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  error: GenerateHeatsError | null;
  /** Vocabulaire « vagues » au lieu de « heats » sur les événements course. */
  isCourseEvent: boolean;
}

/**
 * Échec de génération : ce qui manque, et quoi changer pour y arriver.
 *
 * Le backend renvoie ces compteurs plutôt qu'un simple message : sans eux,
 * l'organisateur voyait « génération impossible » sans savoir s'il devait
 * ajouter un jour, élargir les créneaux ou réduire la durée.
 */
const GenerateErrorDialog: React.FC<Props> = ({ open, onOpenChange, error, isCourseEvent }) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-destructive">
            {isCourseEvent ? t('heats.generateErrorDialog.titleWaves') : t('heats.generateErrorDialog.title')}
          </DialogTitle>
        </DialogHeader>

        {error && (
          <div className="py-4 space-y-4">
            <p className="text-sm">{error.error}</p>

            {/* Les compteurs ne valent que pour un refus de PLACEMENT. Sur un
                refus de données — des équipes sans catégorie — ils vaudraient
                tous zéro et brouilleraient le message. */}
            {error.total_heats_needed !== undefined && (
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Badge variant="outline">{error.total_heats_placed}</Badge>
                <span className="text-muted-foreground">
                  {isCourseEvent ? t('heats.generateErrorDialog.wavesPlaced') : t('heats.generateErrorDialog.heatsPlaced')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{error.total_heats_needed}</Badge>
                <span className="text-muted-foreground">
                  {isCourseEvent ? t('heats.generateErrorDialog.wavesNeeded') : t('heats.generateErrorDialog.heatsNeeded')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="destructive">{error.shortage_heats}</Badge>
                <span className="text-muted-foreground">{t('heats.generateErrorDialog.heatsMissing')}</span>
              </div>
            </div>
            )}

            {error.suggestions && error.suggestions.length > 0 && (
              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Lightbulb size={16} className="text-muted-foreground" />
                  {t('heats.generateErrorDialog.suggestions')}
                </p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {error.suggestions.map((suggestion, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-foreground">•</span>
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>{t('heats.actions.understood')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateErrorDialog;
